import { memo, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { AbsoluteFill } from 'remotion';

import { segmentationsDiffMapAtom, syncedPositionAtom } from '@store';
import { u } from '@ui';

import { useOptions } from './context';
import { Video, VideoCell } from './VideoCell';
import { VideoEmpty } from './VideoEmpty';

interface VideoLayoutProps {
    videos: Video[];
}

export const VideoLayout: React.FC<VideoLayoutProps> = ({ videos }) => {
    const { columns } = useOptions();
    const segmentationsDiffMap = useAtomValue(segmentationsDiffMapAtom);
    const syncedPosition = useAtomValue(syncedPositionAtom);
    const frameDiff = segmentationsDiffMap[syncedPosition] ?? 0;
    const safeFrameDiff = Number.isFinite(frameDiff) ? frameDiff : 0;
    const isSingleVideo = videos.length === 1;

    // AbsoluteFill component overrides className styles, so we must set it through style prop.
    const rawGridStyle = useMemo(
        () => ({
            display: 'grid',

            ...(isSingleVideo
                ? { gridTemplateColumns: `1fr`, gridTemplateRows: '100%' }
                : { gridTemplateColumns: `repeat(${columns}, 1fr)`, gridTemplateRows: '50% 50%' }),
            gap: '0px',
            backgroundColor: 'black',
            borderRadius: u(1),
            overflow: 'hidden',
        }),
        [columns, isSingleVideo],
    );

    return (
        <AbsoluteFill style={rawGridStyle}>
            {videos.map((video, i) => {
                if (!video) return <VideoEmpty key={i} />;
                const diff = video?.type === 'comparison' ? safeFrameDiff : 0;
                return <VideoCell key={video.url} video={video} diff={diff} />;
            })}
        </AbsoluteFill>
    );
};

export const MemoVideoLayout = memo(VideoLayout);
